import * as React from 'react';
import { Breakpoint, getClientWidth } from '@tock/shared/utils/responsive';
import { Button, NumericChipLabel } from 'omakase';
import {
  HistorySeam,
  ImageHistory,
  getRootImageFlowPagePath,
} from 'routes/business/imageHistoryUtil';
import { LegacyPlaceholderBusinessImage, centerCropUrl } from '@tock/images';
import { Route, useHistory, useRouteMatch } from 'react-router-dom';
import { chipLabel } from 'components/ui/largeImageGalleryCssDefs';
import { core } from '@tock/coretypes';
import { getPerceivedBrightnessFromHex } from 'utils/colorUtils';
import { identity, orderBy, values } from 'es-toolkit/compat';
import { joinPath } from 'utils/pathUtils';
import GalleryImage from 'components/ui/GalleryImage';
import ImageListRoute from 'routes/business/ImageListRoute';
import clsx from 'clsx';

interface WidthHeight {
  width: number;
  height: number;
}

interface ImageProportions {
  large: WidthHeight;
  small: WidthHeight;
}

export interface MatchParams {
  businessDomain?: string;
  offering?: string;
  experienceId?: string;
}

interface BannerImages {
  largeImage?: core.UploadedImage;
  smallImages?: core.UploadedImage[];
}

type ShowNumber = 1 | 3;
type BusinessInfo = { name: string; city: string };
type Experience = { name: string };
interface LargeImageGalleryProps {
  business: BusinessInfo;
  experience?: Experience;
  aspect?: number;
  images: core.UploadedImage[];
  isLightboxEnabled?: boolean;
  show: ShowNumber;
  onClickImage?(action?: string): void;
  forceShowChipLabel?: boolean;
}

const getBannerImages = (
  numImages: number,
  show: ShowNumber,
  images: core.UploadedImage[],
  aspect: number,
  useBrowserWidth: boolean
): BannerImages | undefined => {
  // Helper method to retrieve image proportions
  function getImageProportions(options: {
    aspectRatio: number;
    screenWidthPercentage: number;
    useBrowserWidth: boolean;
  }): ImageProportions {
    const { aspectRatio, screenWidthPercentage, useBrowserWidth } = options;

    let largeWidth = 400;
    if (BROWSER && useBrowserWidth) {
      const clientWidth = getClientWidth();
      largeWidth = Math.max(Math.min(clientWidth, 2400) * screenWidthPercentage, 600);
    }
    const smallWidth = Math.max(Math.ceil(largeWidth / 2), 600);

    return {
      large: {
        width: Math.ceil(largeWidth),
        height: Math.ceil(largeWidth * aspectRatio),
      },
      small: {
        width: Math.ceil(smallWidth),
        height: Math.ceil(smallWidth * aspectRatio),
      },
    };
  }

  // use browser width when not rendering on server
  if (numImages < 1) {
    return;
  }
  if (show === 1) {
    const { large } = getImageProportions({
      aspectRatio: 1 / aspect,
      screenWidthPercentage: 1,
      useBrowserWidth,
    });
    return {
      largeImage: {
        ...images[0],
        imageUrl: centerCropUrl(images[0].imageBackingUrl, {
          width: large.width,
          height: large.height,
        }),
      },
    };
  } else {
    const { large, small } = getImageProportions({
      aspectRatio: 0.75,
      screenWidthPercentage: 0.67,
      useBrowserWidth,
    });
    return {
      largeImage: {
        ...images[0],
        imageUrl: centerCropUrl(images[0].imageBackingUrl, {
          width: large.width,
          height: large.height,
        }),
      },
      smallImages: [images[1], images[2]].map((image) => {
        return {
          ...image,
          imageUrl: centerCropUrl(image.imageBackingUrl, {
            width: small.width,
            height: small.height,
          }),
        };
      }),
    };
  }
};

const getDisplayImages = (images: core.UploadedImage[]) =>
  images.map((image) => {
    return {
      id: image.id,
      alt: image.altText,
      src: image.imageUrl,
      srcSet: getSrcSetFromImageURL(image.imageBackingUrl).join(),
    };
  });

const { mPortrait, ...imageWidths } = Breakpoint;
const sizes = orderBy(values(imageWidths), identity, 'desc');

const getSrcSetFromImageURL = (imageUrlInput: string) => {
  return sizes.map((size) => `${centerCropUrl(imageUrlInput, { width: size })} ${size}w`);
};

export const IMAGE_LIST_PATH = '/images';

const LargeImageGallery: React.FunctionComponent<LargeImageGalleryProps> = ({
  business,
  aspect = 2,
  images,
  isLightboxEnabled = true,
  show,
  onClickImage,
  forceShowChipLabel = false,
}) => {
  // Compute full-sized client side images
  const [clientLargeBannerImage, setClientLargeBannerImage] = React.useState<
    core.UploadedImage | undefined
  >(undefined);
  const [clientSmallBannerImages, setClientSmallBannerImages] = React.useState<
    core.UploadedImage[] | undefined
  >(undefined);

  const history = useHistory<ImageHistory>();
  const match = useRouteMatch<MatchParams>();

  const numImages = images.length;

  React.useEffect(() => {
    const clientGeneratedImages = getBannerImages(numImages, show, images, aspect, true);
    setClientLargeBannerImage(clientGeneratedImages?.largeImage);
    setClientSmallBannerImages(clientGeneratedImages?.smallImages);
  }, [aspect, images, numImages, show]);

  const displayImages = React.useMemo(() => getDisplayImages(images), [images]);

  // Banner image handlers
  const handleBannerImageClick = () => {
    if (onClickImage) {
      onClickImage();
    }

    HistorySeam.push(history, joinPath(match.url, IMAGE_LIST_PATH), { fromBusiness: true });
  };

  // ImageList handlers
  const handleImageListClose = () => {
    if (history.location.state?.fromBusiness) {
      HistorySeam.goBack(history);
    } else {
      const rootPath = getRootImageFlowPagePath(match.url);
      HistorySeam.replace(history, rootPath);
    }
  };

  const serverGeneratedImages = getBannerImages(numImages, show, images, aspect, false);

  // TODO: As work progresses in this component, let's convert all styles to use Emotion and remove the need for class names
  const LargeImageGalleryClassname = clsx('LargeImageGallery', {
    'LargeImageGallery--lightboxEnabled': isLightboxEnabled && numImages > 1,
  });
  const LargeImageGalleryContainerClassname = clsx('LargeImageGallery-largeImageContainer', {
    'LargeImageGallery-largeImageContainer--banner': !serverGeneratedImages?.smallImages,
  });

  let innerContent: React.ReactNode;
  const largeImage = clientLargeBannerImage ?? serverGeneratedImages?.largeImage;
  const smallImages = clientSmallBannerImages ?? serverGeneratedImages?.smallImages;
  if (largeImage) {
    const { altText, dominantColor, imageUrl } = largeImage;

    innerContent = (
      <>
        <div className={LargeImageGalleryContainerClassname}>
          <GalleryImage
            index={0}
            onImageClick={isLightboxEnabled ? handleBannerImageClick : undefined}
            alt={altText}
            backgroundColor={dominantColor}
            imageUrl={imageUrl}
            className="LargeImageGallery-largeImage"
            isLink={!!isLightboxEnabled}
          >
            {isLightboxEnabled && show > 1 && numImages > 1 && (
              <Button
                className="LargeImageGallery-viewMore"
                size="medium"
                aria-label={`View all ${numImages} photos (opens a dialog)`}
                variant="basicBorderless"
                data-testid="view-all-photos-button"
              >
                View all {numImages} photos
              </Button>
            )}
          </GalleryImage>
        </div>
        {smallImages && (
          <div className="LargeImageGallery-smallImageContainer">
            <GalleryImage
              index={1}
              alt={smallImages[0].altText}
              backgroundColor={smallImages[0].dominantColor}
              onImageClick={handleBannerImageClick}
              className="LargeImageGallery-smallImage"
              imageUrl={smallImages[0].imageUrl}
              isLink
              data-testid="GalleryImage"
            />
            <GalleryImage
              index={2}
              alt={smallImages[1].altText}
              backgroundColor={smallImages[1].dominantColor}
              onImageClick={handleBannerImageClick}
              className="LargeImageGallery-smallImage"
              imageUrl={smallImages[1].imageUrl}
              isLink
            />
          </div>
        )}
      </>
    );
  } else {
    innerContent = (
      <div className={LargeImageGalleryContainerClassname}>
        <GalleryImage
          index={0}
          alt={'Image loading'}
          imageUrl={LegacyPlaceholderBusinessImage}
          data-testid={'gallery-large-image'}
          className={clsx('LargeImageGallery-largeImage', 'is-placeholder')}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={LargeImageGalleryClassname}
        style={{ paddingBottom: `${(1 / aspect) * 100}%` }}
      >
        <div className="LargeImageGallery-inner">{innerContent}</div>
        {numImages > 1 && (
          <NumericChipLabel
            data-testid="image-count-chip"
            aria-label={`Image 1 of ${numImages}`}
            variant="basic"
            current={1}
            total={numImages}
            css={chipLabel(forceShowChipLabel)}
            highContrast={
              getPerceivedBrightnessFromHex(largeImage?.dominantColor ?? '#fff') === 'light'
            }
          />
        )}
      </div>
      <Route path={joinPath(match.path, IMAGE_LIST_PATH)}>
        <ImageListRoute
          businessName={business.name}
          images={displayImages}
          onClose={handleImageListClose}
        />
      </Route>
    </>
  );
};

export default LargeImageGallery;
